class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.buyNowButton = this.querySelector('[data-buy-now]');
    this.buyEmiButton = this.querySelector('[data-buy-emi]');
    this.errorContainer = this.querySelector('[data-form-error]');
    this.notifyMeButton = this.querySelector('[data-notify-me-trigger]');
    this.secondaryActions = this.querySelector('[data-secondary-actions]');

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.buyNowButton?.addEventListener('click', (event) => this.onBuyNow(event));
    this.buyEmiButton?.addEventListener('click', () => this.onBuyEmi());
    this.initQuantityStepper();
    this.initVariantSubscriber();

    // Back/forward navigation restores this exact DOM from bfcache rather than
    // reloading it, so a loader left spinning before the checkout redirect
    // would otherwise stay stuck forever.
    this.handlePageShow = (event) => {
      if (event.persisted) this.setLoading(false);
    };
    window.addEventListener('pageshow', this.handlePageShow);
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  initVariantSubscriber() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
      if (data.productId !== this.dataset.productId) return;

      const variantInput = this.form.querySelector('input[name="id"]');
      const buttonText = this.submitButton?.querySelector('[data-button-text]');

      if (data.variant) {
        if (variantInput) variantInput.value = data.variant.id;

        // The add path reads these off this element, so they must track the
        // chosen variant — not the product's featured image and default price
        // Liquid rendered. Guarded: a variant with no featured image publishes
        // an empty string, which would wipe the product-level fallback.
        if (data.variant.price_formatted) this.dataset.productPrice = data.variant.price_formatted;
        if (data.variant.image) this.dataset.productFeaturedImage = data.variant.image;

        if (this.submitButton && buttonText) {
          if (data.variant.available) {
            this.submitButton.disabled = false;
            // Cards pass their own label ("ADD"); keep it instead of the full-size default.
            buttonText.textContent =
              buttonText.dataset.availableLabel ?? window.theme?.strings?.addToCart ?? 'Add to cart';
          } else {
            this.submitButton.disabled = true;
            buttonText.textContent = window.theme?.strings?.soldOut ?? 'Sold out';
          }
        }

        if (this.buyNowButton) this.buyNowButton.disabled = !data.variant.available;
        if (this.buyEmiButton) this.buyEmiButton.disabled = !data.variant.available;

        if (this.notifyMeButton) {
          if (this.submitButton) this.submitButton.hidden = !data.variant.available;
          this.notifyMeButton.hidden = data.variant.available;
          if (this.secondaryActions) this.secondaryActions.hidden = !data.variant.available;
        }
      } else {
        if (this.submitButton && buttonText) {
          this.submitButton.disabled = true;
          buttonText.textContent = window.theme?.strings?.unavailable ?? 'Unavailable';
        }

        if (this.buyNowButton) this.buyNowButton.disabled = true;
        if (this.buyEmiButton) this.buyEmiButton.disabled = true;
      }
    });
  }

  initQuantityStepper() {
    const input = this.querySelector('input[name="quantity"]');
    if (!input) return;

    const minusBtn = this.querySelector('[data-quantity-minus]');

    const updateMinusState = () => {
      if (!minusBtn) return;
      const currentQty = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const atMin = currentQty <= min;
      minusBtn.classList.toggle('text-muted-grey', atMin);
      minusBtn.classList.toggle('text-ink', !atMin);
    };

    this.addEventListener('click', (event) => {
      const minusClicked = event.target.closest('[data-quantity-minus]');
      const plusClicked = event.target.closest('[data-quantity-plus]');

      if (!minusClicked && !plusClicked) return;

      const currentQty = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 99;

      if (minusClicked) {
        input.value = Math.max(min, currentQty - 1);
      } else if (plusClicked) {
        input.value = Math.min(max, currentQty + 1);
      }

      updateMinusState();
    });

    input.addEventListener('input', updateMinusState);
    updateMinusState();
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.submitButton?.disabled) return;
    this.setLoading(true);
    this.hideError();

    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData(this.form);

    try {
      // No `control` — setLoading already owns this button's loader. `source` is not
      // a control: it hands the drawer this element's product data for the optimistic
      // row without handing it the button's loading state.
      if (cartDrawer) {
        cartDrawer.open();
        // No error handling here: the drawer's mutation loop has already painted
        // the localised message into its own error slot.
        await cartDrawer.addFromForm(formData, { source: this });
      } else {
        await CartAPI.add(formData);
      }
    } catch (error) {
      this.showError(this.errorMessage(error));
    } finally {
      this.setLoading(false);
    }
  }

  errorMessage(error) {
    return error?.description ?? error?.message ?? window.theme?.strings?.cartError;
  }

  async onBuyNow(event) {
    event.preventDefault();

    if (this.buyNowButton?.disabled || this.submitButton?.disabled) return;

    this.setLoading(true, this.buyNowButton);
    this.hideError();

    const formData = new FormData(this.form);

    try {
      await CartAPI.add(formData);
      window.location.href = this.dataset.checkoutUrl;
    } catch (error) {
      this.showError(this.errorMessage(error));
      this.setLoading(false);
    }
  }

  onBuyEmi() {
    if (this.buyEmiButton?.disabled) return;

    const emiSummary = document.querySelector('.emi-summary');
    if (!emiSummary) return;

    const trigger = emiSummary.querySelector('[data-accordion-trigger]');
    if (trigger?.getAttribute('aria-expanded') === 'false') trigger.click();

    emiSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  setLoading(isLoading, activeButton = this.submitButton) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    if (this.buyNowButton) this.buyNowButton.disabled = isLoading;

    this.toggleButtonLoader(this.submitButton, isLoading && activeButton === this.submitButton);
    this.toggleButtonLoader(this.buyNowButton, isLoading && activeButton === this.buyNowButton);
  }

  toggleButtonLoader(button, isLoading) {
    if (!button) return;

    const buttonText = button.querySelector('[data-button-text]');
    const buttonDot = button.querySelector('[data-cta-dot]');
    const buttonLoader = button.querySelector('[data-button-loader]');

    if (buttonText) buttonText.classList.toggle('invisible', isLoading);
    if (buttonDot) buttonDot.classList.toggle('invisible', isLoading);
    if (buttonLoader) {
      buttonLoader.classList.toggle('hidden', !isLoading);
      buttonLoader.classList.toggle('flex', isLoading);
    }
  }

  showError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
  }

  hideError() {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = '';
    this.errorContainer.hidden = true;
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
